import { Button, ButtonProps } from "@mui/material";
import { MuiForm5 as Form } from '@rjsf/material-ui';
import { ErrorSchema, FormProps, IChangeEvent, ISubmitEvent } from "@rjsf/core";
import { ButtonSubmit } from "components/button/ButtonSubmit";
import { DialogType } from "components/dialog/GenericDialog";
import  { GenericFormTitle } from "components/forms/components/title/GenericFormTitle";
import { RadioPremium } from "./components/premium/RadioPremium";
import { TextFieldPremium } from "./components/premium/TextFieldPremium";
import { SelectCountry, SelectLanguage, SelectCountryLanguage } from "./components/select-country";
import { UploadFile } from "./components/upload-file/UploadFile";
import { useGenericForm } from "./hooks/useGenericForm";
import './generic-form.scss'
import { CardSimpleForm } from "./components/card/CardSimpleForm";
import { Dispatch, forwardRef, useImperativeHandle, useMemo } from "react";
import { EcoCheckbox } from "components";
import { CountryCombo } from "./components/country/CountryCombo";

/** 
 * Render Generic form usign JSON data.
 * 
 * -----------------------------------------------------------------------
 * 
 * Custom Fields
 * 
 * -----------------------------------------------------------------------
 * 
 * Custom fields can be add inside generic form. 
 * 
 * Just declare component inside "fields" constant with an unique name.
 * 
 * Then use uiSchema to use the custom component.
 * 
 * eg :
 *  
 * ```
 * {
 *      title: "MY PRODUCT",
 *      subTitle: "COUNTRY - LANGUAGE",
 *      content: {
 *          type: 'object',
 *          properties: {
 *              productName: { type: 'string', title: 'Product Name' },
 *              productUse: { type: 'string', title: 'Product Usage' },
 *              productCountry: { 
 *                  type: 'string', 
 *                  title: 'Country',
 *              },
 *          }
 *      },
 *      uiSchema: {
 *          productCountry: {
 *              "ui:field": "SelectCountry"
 *          }
 *      }
 *  }
 * ```
 * 
 * Warning :
 * - Make sure that the component call "onChange" passed by props when control change so rjsf can store the control value somewhere
 * - "Custom Widget" just override component base on "string" | "number" | "integer" | "boolean" and doesn't create a new field
 * For more information, please, check this documentation https://react-jsonschema-form.readthedocs.io/en/latest/advanced-customization/custom-widgets-fields/
 * 
 * ###Import to use it
 *
 *```import { GenericForm } from "@eco/react-lib"```
 *
 *### Basic Example
 */
export const GenericForm = forwardRef((props: GenericFormType, ref?: React.Ref<GenericFormRef | undefined>) => {
    const {
        value,
        setValue,
        isDirty,
        dirtiesFields,
        renderDialog,
        handleFormSubmit,
        handleFormChange,
        handleBackClick
    } = useGenericForm(props);

    // Define the custom field component to use for the root object
    const fields = {
        SelectCountry,
        SelectLanguage,
        SelectCountryLanguage,
        UploadFile,
        RadioPremium,
        TextFieldPremium,
        CardSimpleForm,
        EcoCheckbox,
        CountryCombo,
        ...props.fields
    }

    const canShowHeader = useMemo(() => props.schema.title || props.schema.subTitle || props.schema.description, [props.schema])
    
    useImperativeHandle(ref, () => ({
        value,
        setValue,
        isDirty,
        dirtiesFields
    }))

    const { btnCancel, btnSubmit } = props.schema

    return (
        <div className="form">
            {
                canShowHeader &&
                <div className="form-header">
                    <GenericFormTitle
                        title={props.schema.title as(string)}
                        subTitle={props.schema.subTitle as(string)}
                        description={props.schema.description as(string)}
                        tooltip={props.schema.tooltip as DialogType}
                        />
                </div>
            }
            <div className="form-container">
                { props.icon && <img className="form-icon" src={props.icon} />}
                <Form
                    {...props}
                    formData={value}
                    className={`form-body ${props.className}`}
                    fields={fields}
                    onChange={handleFormChange}
                    onSubmit={handleFormSubmit}>
                    { props.imgSrc && <img className="form-img" src={props.imgSrc} /> }
                    <div className="form-footer">
                        {
                            !props.hideFooter &&
                            <>
                                {
                                    btnCancel && 
                                    <Button 
                                        {...btnCancel}
                                        className={`btn-default mr-auto ${btnCancel.className}`}
                                        variant="contained"
                                        type="button"
                                        onClick={handleBackClick}>
                                        {btnCancel.text}
                                    </Button>
                                }
                                
                                {
                                    btnSubmit &&
                                    <ButtonSubmit
                                        disabled={(!(!props.isDirtyUx || (props.isDirtyUx && isDirty)))}
                                        {...btnSubmit}
                                        label={btnSubmit.text}
                                        className={btnSubmit.className}
                                        isSubmit={props.isSubmiting || props.isLoading}
                                        />
                                }
                                {
                                    props.btnSubmit && !btnSubmit &&
                                    <ButtonSubmit
                                        disabled={(!(!props.isDirtyUx || (props.isDirtyUx && isDirty)))}
                                        {...props.btnSubmit}
                                        isSubmit={props.isSubmiting || props.isLoading}
                                        />
                                }
                            </>
                        }
                    </div>
                </Form>
                
            </div>

            { renderDialog() }
        </div>
    )
})

export type GenericFormButtonType = ButtonProps & {
    /** Text inside the button */
    label: string
}


export type GenericFormType<T=any> = FormProps<T> & { 
    /** 
     * Form unique key
     * It's used for a global dirty management
     */     
    key?: string,
    /** 
     * Form to render dynamicaly
     * Please, refer to https://react-jsonschema-form.readthedocs.io/en/latest/ 
     */
    schema: FormProps<T>['schema'] & Record<string, any>
    /** Flag telling if showing back button */
    canShowBackBtn?: boolean,
    /** Dialog before submiting forms */
    dialog?: DialogType,
    /** Flag telling if hidding footer */
    hideFooter?: boolean,
    /* class for from*/
    className?: string,
    /* icon for form*/
    icon?: string,
    /* img for form*/
    imgSrc?: string,
    /** Flag indicating that all elements must be readonly */
    readonly?: boolean
    /** Button to unblock readonly state */
    btnUnblockReadonly?: GenericFormButtonType,
    /** Button submit parameters */
    btnSubmit?: GenericFormButtonType,
    /** Button submit parameters */
    btnCancel?: GenericFormButtonType,
    /** Callback when user clicked on back button */
    onBack?: () => void,
    /** 
     * Flag telling if is submiting
     * @deprecated Use isSubmiting instead
     */
    isLoading?: boolean
    /**
     * Flag telling that the form is submiting
     */
    isSubmiting?: boolean
    /** 
     * Flag telling that the component wait for changes before enabling submit button
     * It only work with uncontrolled component
     * If activated, it will sync to global dirty form management
     */
    isDirtyUx?: boolean
    /** Callback when component is about to change */
    onChange?: (value?: T, e?: IChangeEvent<T>, es?: ErrorSchema, dirtiesFields?: Array<string>) => void,
    /** Callback when component is about to submit */
    onSubmit?: (value?: T, e?: ISubmitEvent<T>, dirtiesFields?: Array<string>) => void | Promise<void>;
    /** 
     * Value of form. Use it for controlled form
     * Please, refer to https://reactjs.org/docs/forms.html#controlled-components
     */
    value?: T,
    /** 
     * Form default value. Use it for uncontrolled form 
     * Please, refer to https://reactjs.org/docs/uncontrolled-components.html
     */
    defaultValue?: T
}

export type GenericFormProps = GenericFormType

export type GenericFormRef = {
    value: any
    setValue: Dispatch<any>
    isDirty: boolean
    dirtiesFields?: Array<string>
}

GenericForm.defaultProps = {
    canShowBackBtn: true,
    liveValidate: false,
    uiSchema: {},
    showErrorList: false,
    isLoading: false,
}
